// ── 상황판 직원 본인 위젯 — 큰 카드 패턴 (2026-06-07 재작성) ─────────────────
//
// UnifiedCard / AdminWidgetCard 와 동일한 큰 카드 디자인:
//   - 카드 배경: 흰색 (CARD_BG)
//   - 테두리: border border-{accent}-100 (옅음)
//   - 상단 강조: border-t-[3px] border-t-{accent}-400
//   - 라벨: text-[12px] font-bold text-[var(--text-sub)]
//   - 값: text-xs font-black + accent text (사용자 명세 — 작게 통일)
//   - hover: brightness-95
//   - width:
//       'narrow' (기본) → col-span-2 (작은 셀, 모바일/데스크탑 동일 통일)
//       'wide'           → col-span-2 (모든 화면)
//
// 2026-06-06 monthNavigate / month / onMonthChange / monthLabel props 유지.
// 2026-06-06 드래그 정렬 지원 — sortableId props.
// PersonalMiniSection 의 grid 안에서 col-span-2 부여.

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '../../../../lib/utils';
import {
  accentText,
  CARD_BG, DEFAULT_ACCENT,
} from '../widgetAccent';

// 월 이동 헬퍼 — YYYY-MM ± n
function shiftMonth(ym, delta) {
  if (!/^\d{4}-\d{2}$/.test(ym || '')) return ym;
  const [y, m] = ym.split('-').map(Number);
  const zb = (y * 12) + (m - 1) + delta;
  const ny = Math.floor(zb / 12);
  const nm = (zb % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

/**
 * MiniRow — 본인 위젯 한 줄 카드 (큰 카드 패턴 적용)
 *
 * Props:
 *   icon: Lucide React 아이콘 컴포넌트
 *   label: 위젯 라벨
 *   value: 핵심 숫자/상태 텍스트
 *   accent: 색상 키 (위젯 기본값)
 *   accentOverride: 사용자가 설정 모달에서 지정한 색상 (우선 적용)
 *   loading / denied
 *   onClick
 *   width: 'narrow' (기본) / 'wide' (col-span-2 동일 — 큰 카드 패턴 통일)
 *   children: value 영역 직접 채우기 (예: 출퇴근 버튼)
 *   monthNavigate / month / onMonthChange / monthLabel
 *   sortableId: 드래그 정렬용 ID
 */
export function MiniRow({
  icon: Icon,
  label,
  value,
  accent = DEFAULT_ACCENT,
  accentOverride,
  loading,
  denied,
  onClick,
  width = 'narrow',
  size = 'S',
  children,
  monthNavigate = false,
  month,
  onMonthChange,
  monthLabel,
  sortableId,
  watermarkIcon,
}) {
  const accentKey = accentOverride || accent || DEFAULT_ACCENT;
  const txt = accentText(accentKey);
  const clickable = Boolean(onClick) && !denied;

  // dnd-kit sortable — sortableId 가 falsy 면 비활성.
  const sortable = useSortable({ id: sortableId || `__nosort_${label || 'x'}`, disabled: !sortableId });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
  const dragStyle = sortableId
    ? { transform: CSS.Transform.toString(transform), transition }
    : undefined;

  const handlePrev = (e) => {
    e.stopPropagation();
    if (!monthNavigate || !onMonthChange) return;
    onMonthChange(shiftMonth(month || '', -1));
  };
  const handleNext = (e) => {
    e.stopPropagation();
    if (!monthNavigate || !onMonthChange) return;
    onMonthChange(shiftMonth(month || '', 1));
  };
  const defaultMonthLabel = (ym) => {
    if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return '';
    const [, mm] = ym.split('-');
    return `${Number(mm)}월`;
  };
  const labelFn = typeof monthLabel === 'function' ? monthLabel : defaultMonthLabel;

  // width 는 시각 구분 없이 spec 호환용 — size 가 실제 분기.
  void width;

  // size 분기 (사용자 명세 — 모든 row 같은 높이 220px 기준):
  //   S = col-span-1 row-span-1 (220×220 정사각)
  //   M = col-span-2 row-span-1 (460×220 가로 직사각)
  //   L = col-span-2 row-span-2 (460×460 정사각)
  const cellSpan = size === 'S'
    ? 'col-span-1 row-span-1'
    : size === 'L'
      ? 'col-span-2 row-span-2'
      : 'col-span-2 row-span-1';

  // 사용자 명세 (2026-06-07): S = 디자이너 풍 — 라벨 상단 / 큰 값 중앙 / 부가 정보 하단.
  // watermark 는 spec 가 전달한 watermarkIcon 우선, 없으면 메인 icon 사용.
  const isS = size === 'S';
  const Watermark = watermarkIcon || Icon;
  // S 패딩 px-4 py-4, M/L 패딩 px-4 py-3
  const padCls = isS ? 'px-4 py-4' : 'px-4 py-3';

  return (
    <div
      ref={sortableId ? setNodeRef : undefined}
      style={dragStyle}
      onClick={clickable ? onClick : undefined}
      {...(sortableId ? attributes : {})}
      {...(sortableId ? listeners : {})}
      className={cn(
        // size 별 그리드 셀 분기 — 데스크탑/모바일 통일 (정사각 비율)
        cellSpan,
        // 2026-08-25 재스킨(사용자 명세): 색 띠·색 테두리 제거 — 중립 테두리+둥근 모서리+호버 그림자.
        // accent 는 아이콘·수치·워터마크 색에만.
        'rounded-2xl border border-[var(--border)] transition-shadow min-w-0 relative overflow-hidden',
        padCls,
        isS ? 'flex flex-col justify-between h-full' : 'flex items-start gap-2',
        CARD_BG,
        'hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]',
        clickable && 'cursor-pointer',
        denied && 'opacity-50',
        // 드래그 가능 시 grab 커서. drag 중 강조.
        sortableId && !clickable && 'cursor-grab active:cursor-grabbing',
        sortableId && isDragging && 'opacity-50 shadow-lg z-10 ring-2 ring-blue-400'
      )}
    >
      {/* watermark — 우측 하단, 옅게 (S 사이즈만) */}
      {isS && Watermark && (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute -bottom-2 -right-2 opacity-10',
            txt,
          )}
        >
          <Watermark size={88} strokeWidth={2} />
        </span>
      )}

      {/* 헤더 — 라벨 + (월 nav) */}
      <div className={cn(
        'relative z-[1] flex items-center gap-1.5 min-w-0',
        isS && 'w-full',
      )}>
        {Icon && (
          <span className={cn('flex-shrink-0', txt)}>
            <Icon size={16} strokeWidth={2.25} />
          </span>
        )}
        <span className={cn('text-[12px] font-bold text-[var(--text-sub)] whitespace-nowrap')}>{label}</span>
        {monthNavigate && (
          <button
            type="button"
            aria-label="이전 달"
            onClick={handlePrev}
            onPointerDown={(e) => e.stopPropagation()}
            className="shrink-0 p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5 text-[var(--text-sub)]"
          >
            <ChevronLeft size={12} />
          </button>
        )}
        {monthNavigate && (
          <span className="text-[10px] text-[var(--text-sub)] shrink-0 tabular-nums">
            {labelFn(month || '')}
          </span>
        )}
        {monthNavigate && (
          <button
            type="button"
            aria-label="다음 달"
            onClick={handleNext}
            onPointerDown={(e) => e.stopPropagation()}
            className="shrink-0 p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5 text-[var(--text-sub)]"
          >
            <ChevronRight size={12} />
          </button>
        )}
        {/* M/L 한 줄: 우측 값 영역 (S 에서는 본문으로 분리) */}
        {!isS && (
          <div className="ml-auto flex items-baseline gap-1 justify-end flex-shrink-0 whitespace-nowrap tabular-nums">
            {children
              ? children
              : loading ? <span className="text-[11px] text-[var(--text-muted)]">…</span>
              : denied ? <span className="text-[11px] text-[var(--text-muted)]">권한</span>
              : <span className={cn('text-xs font-black whitespace-nowrap', txt)}>{value}</span>
            }
          </div>
        )}
      </div>

      {/* S 본문: 큰 값 중앙 (호출처가 children 으로 큰 폰트 전달) */}
      {isS && (
        <div className="relative z-[1] flex-1 flex flex-col justify-center items-end min-w-0 mt-2 tabular-nums">
          {children
            ? children
            : loading ? <span className="text-[11px] text-[var(--text-muted)]">…</span>
            : denied ? <span className="text-[11px] text-[var(--text-muted)]">권한</span>
            : <span className={cn('text-2xl font-black whitespace-nowrap', txt)}>{value}</span>
          }
        </div>
      )}
    </div>
  );
}
